import { Injectable } from '@angular/core'
import * as lodash from 'lodash'

import { from, Observable, of, combineLatest } from 'rxjs'
import { catchError, map, switchMap, take } from 'rxjs/operators'
import {
  Account,
  ActionFailed,
  Rental,
  RentalService,
  selectCurrentAccount,
  selectOnce,
  selectRentalById,
  selectSomeOnce,
  selectUserEntities,
  userFullname,
} from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import { selectAllCustomCodes } from '@tv3/store/custom-codes/custom-code.selectors'
import { GuestService } from '@tv3/store/guest/guest.service'
import {
  Contract,
  ContractSigner,
  ContractSignerType,
  SignFieldDef,
  SignFieldType,
  TemplateTypes,
} from '@tokeet-frontend/signature'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Guest } from '@tv3/store/guest/guest.model'
import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import { HttpErrorResponse } from '@angular/common/http'
import { DataTokenDef, SignatureDataDictHelperService } from '@tv3/services/signature-data-dict-helper.service'

@Injectable({ providedIn: 'root' })
export class SendContractService {
  private usersById$ = this.store.pipe(select(selectUserEntities))
  private codes$ = this.store.pipe(select(selectAllCustomCodes))

  constructor(
    private store: Store<any>,
    private dataDicHelperService: SignatureDataDictHelperService,
    private rentalService: RentalService,
    private guestService: GuestService
  ) {}

  hasDataTokenFields(autoFilledFields: SignFieldDef[]): boolean {
    return !!lodash.find(autoFilledFields, (field) => field.type === SignFieldType.DataDic)
  }

  getAutoFilledFields(type: TemplateTypes, fields: SignFieldDef[] = []): SignFieldDef[] {
    let dataDictFields = lodash.filter(fields, { type: SignFieldType.DataDic })
    dataDictFields = lodash.filter(dataDictFields, (field) => {
      return !field.role || field.readonly === true // no role associated fields
    })
    return dataDictFields
  }

  isBookingRequired(type: TemplateTypes, fields: SignFieldDef[]) {
    const autoFilledFields = this.getAutoFilledFields(type, fields)
    const autoFilledFieldsWithoutRole = autoFilledFields.filter((f) => !f.role)
    return this.hasDataTokenFields(autoFilledFieldsWithoutRole)
  }

  fillContactFields(type: TemplateTypes, signers: ContractSigner[], autoFilledFields: SignFieldDef[]) {
    const contacts = lodash.filter(signers, (s) => s.type === ContractSignerType.Guest)
    const contactRoles = lodash.map(contacts, (s) => s.role)
    const fields = lodash.filter(autoFilledFields, (f) => contactRoles.includes(f.role))
    if (!fields.length) {
      return of([])
    }

    const contactIds = lodash.map(contacts, (s) => s.id)
    return this.guestService.fetch(contactIds).pipe(
      this.catchRequest404Error($localize`Unable to load guest data in your booking.`),
      map((items) => lodash.keyBy(items, (t) => t.id)),
      switchMap((guestsById) => {
        return from(
          Promise.all(
            contacts.map((c) => {
              const guest = guestsById[c.id]
              const fs = lodash.filter(fields, (f) => f.role === c.role)
              if (!guest || !fs.length) return
              return this.resolveTokens(this.dataDicHelperService.getGuestResolvers([type], guest), fs)
            })
          )
        )
      })
    )
  }

  fillUserFields(type: TemplateTypes, signers: ContractSigner[], autoFilledFields: SignFieldDef[]) {
    const users = lodash.filter(signers, (s) => s.type === ContractSignerType.Staff)
    const userRoles = lodash.map(users, (s) => s.role)
    const fields = lodash.filter(autoFilledFields, (f) => userRoles.includes(f.role))
    if (!fields.length) {
      return of([])
    }

    return this.usersById$.pipe(
      take(1),
      switchMap((usersById) => {
        return from(
          Promise.all(
            users.map((c) => {
              const user = usersById[c.id]
              const fs = lodash.filter(fields, (f) => f.role === c.role)
              if (!user || !fs.length) return
              const guest = user as any
              return this.resolveTokens(
                this.dataDicHelperService.getGuestResolvers([type], { ...guest, name: userFullname(user) }),
                fs
              )
            })
          )
        )
      })
    )
  }

  fillRentalFields(type: TemplateTypes, rentalId: string, autoFilledFields: SignFieldDef[]) {
    const fields = lodash.filter(autoFilledFields, (f) => this.dataDicHelperService.isRentalField(f.attributes?.key))
    if (!fields.length || !rentalId) {
      return of([])
    }
    return this.rentalService.get(rentalId).pipe(
      this.catchRequest404Error($localize`Unable to load rental data in your booking.`),
      switchMap((rental) => {
        return from(this.resolveTokens(this.dataDicHelperService.getRentalResolvers([type], rental), fields))
      })
    )
  }

  fillGuestFields(type: TemplateTypes, guestId: string, autoFilledFields: SignFieldDef[]) {
    const fields = lodash.filter(
      autoFilledFields,
      (f) => !f.role && this.dataDicHelperService.isGuestField(f.attributes?.key)
    )
    if (!fields.length || !guestId) {
      return of([])
    }
    return this.guestService.get(guestId).pipe(
      this.catchRequest404Error($localize`Unable to load guest data in your booking.`),
      switchMap((guest) => {
        return from(this.resolveTokens(this.dataDicHelperService.getGuestResolvers([type], guest), fields))
      })
    )
  }

  fillInquiryFields(type: TemplateTypes, inquiry: Inquiry, autoFilledFields: SignFieldDef[]) {
    const fields = lodash.filter(autoFilledFields, (f) => this.dataDicHelperService.isInquiryField(f.attributes?.key))
    if (!fields.length || !inquiry) {
      return of([])
    }
    return combineLatest([
      this.store.pipe(selectOnce(selectRentalById(inquiry.rentalId))),
      this.store.pipe(selectOnce(selectCurrentAccount)),
    ]).pipe(
      this.catchRequest404Error($localize`Unable to load rental data in your booking.`),
      switchMap(([rental, account]) => {
        return from(
          this.resolveTokens(this.dataDicHelperService.getInquiryResolvers([type], inquiry, rental, account), fields)
        )
      })
    )
  }

  fillCustomCodeFields(type: TemplateTypes, rentalId: string, autoFilledFields: SignFieldDef[]) {
    const fields = lodash.filter(autoFilledFields, (f) =>
      this.dataDicHelperService.isCustomCodeField(f.attributes?.key)
    )
    if (!fields.length || !rentalId) {
      return of([])
    }
    return this.codes$.pipe(
      map((codes) => this.getCustomCodesForRental(codes, rentalId)),
      take(1),
      switchMap((codes) => {
        return from(this.resolveTokens(this.dataDicHelperService.getCustomCodesResolver(codes), fields))
      })
    )
  }

  async resolveTokens(resolvers: { [key: string]: DataTokenDef }, fields: SignFieldDef[]) {
    for (const field of fields) {
      const key = lodash.get(field, 'attributes.key', lodash.get(field, 'label'))
      if (lodash.has(resolvers, key)) {
        field.value = await resolvers[key].get()
      }
    }
  }

  setTokeetDataIds(contractPayload: Partial<Contract>, inquiry: Inquiry) {
    if (inquiry) {
      lodash.set(contractPayload, 'attributes.inquiry_id', inquiry.id)
      lodash.set(contractPayload, 'attributes.rental_id', inquiry.rentalId)
      lodash.set(contractPayload, 'attributes.guest_id', inquiry.guestId)
    } else {
      // set first signer as guest
      const guestSigner = lodash.find(contractPayload.signers, (s) => s.type === ContractSignerType.Guest)
      if (guestSigner) {
        lodash.set(contractPayload, 'attributes.guest_id', guestSigner.id)
      }
    }
  }

  tryToFillWithTokeetData(contractPayload: Partial<Contract>, inquiry: Inquiry): Observable<any> {
    this.setTokeetDataIds(contractPayload, inquiry)
    const autoFilledFields = this.getAutoFilledFields(contractPayload.type, contractPayload.fields)

    return combineLatest([
      this.fillContactFields(contractPayload.type, contractPayload.signers, autoFilledFields),
      this.fillUserFields(contractPayload.type, contractPayload.signers, autoFilledFields),
      ...(inquiry
        ? [
            this.fillCustomCodeFields(contractPayload.type, inquiry?.rentalId, autoFilledFields),
            this.fillGuestFields(contractPayload.type, inquiry?.guestId, autoFilledFields),
            this.fillRentalFields(contractPayload.type, inquiry?.rentalId, autoFilledFields),
            this.fillInquiryFields(contractPayload.type, inquiry, autoFilledFields),
          ]
        : []),
    ])
  }

  async tryToFillWithTokeetBookingData(
    type: TemplateTypes,
    fields: SignFieldDef[],
    data: {
      inquiry?: Inquiry
      rental?: Rental
      guest?: Guest
      customCodes: CustomCode[]
      account?: Account
    }
  ): Promise<any> {
    const { rental, guest, inquiry, customCodes, account } = data
    return Promise.all([
      this.resolveTokens(this.dataDicHelperService.getGuestResolvers([type], guest), fields),
      this.resolveTokens(this.dataDicHelperService.getRentalResolvers([type], rental), fields),
      this.resolveTokens(this.dataDicHelperService.getInquiryResolvers([type], inquiry, rental, account), fields),
      this.resolveTokens(this.dataDicHelperService.getCustomCodesResolver(customCodes), fields),
    ])
  }

  getCustomCodesForRental(customCodes: CustomCode[], rentalId: string) {
    let availableItems = lodash.filter(
      customCodes,
      (c) => lodash.isEmpty(c.rentals) || lodash.includes(c.rentals, rentalId)
    )

    availableItems = lodash.sortBy(availableItems, (i) => (lodash.isEmpty(i.rentals) ? 1 : 0))

    return lodash.uniqBy(availableItems, (i) => i.name)
  }

  private catchRequest404Error(message: string) {
    return catchError<any, any>((error) => {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.store.dispatch(ActionFailed({ error: message }))
      } else {
        this.store.dispatch(ActionFailed({ error }))
      }
      throw error
    })
  }
}
